/**
 * 文件分片上传模块
 *
 * 当文件大小超过阈值时，自动切换到分片上传模式
 */

import { AxiosProgressEvent } from 'axios';
import { Http } from '@mygpt/app/http';

// ===== 配置（硬编码常量；改值直接改这里）=====
// 主要动机：绕过 Cloudflare 100MB body 限制 → 大文件走分片走 backend /files-chunked/*。
// 不引入 env / ConfigMap 控制；按 saas 实际需求迭代时直接改源码。

const CHUNK_UPLOAD_ENABLED = true;   // 总开关
const CHUNK_UPLOAD_THRES_MB = 5;     // 触发阈值（文件总大小超过此值才走分片）
const CHUNK_UPLOAD_SIZE_MB = 5;      // 单分片大小

const CHUNK_SIZE_BYTES = CHUNK_UPLOAD_SIZE_MB * 1024 * 1024;
const THRESHOLD_BYTES = CHUNK_UPLOAD_THRES_MB * 1024 * 1024;

// 单分片失败的重试策略：仅在网络抖动 / 5xx 时重试，4xx 立即失败
const CHUNK_RETRY_MAX_ATTEMPTS = 4;            // 总尝试次数 = 1 次首发 + 3 次重试 → 共 4 次
const CHUNK_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]; // 指数退避（首次失败后等待时长）

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * 判断单分片错误是否值得重试。
 * - 无 response：网络层错误（ERR_NETWORK_CHANGED / 断网 / DNS / 跨域被拦），重试
 * - 5xx：服务端瞬时故障，重试
 * - 4xx：业务错误（参数/权限/会话失效），立即失败
 */
const isRetryableChunkError = (error: any): boolean => {
  if (!error?.response) return true;
  const status = error.response.status;
  return typeof status === 'number' && status >= 500;
};

// ===== 类型定义 =====

export interface ChunkUploadConfig {
  datasetId: string;
  files: File[];
  formData?: FormData; // 原始 FormData，包含其他字段
  onProgress?: (progress: ChunkUploadProgress) => void;
  onError?: (error: Error) => void;
}

export interface ChunkUploadProgress {
  fileIndex: number;
  fileName: string;
  totalFiles: number;
  uploadedChunks: number;
  totalChunks: number;
  percentage: number; // 0-100
}

export interface ChunkUploadResult {
  success: boolean;
  data?: any;
  error?: string;
}

// ===== 分片上传核心类 =====

class ChunkUploader {
  private sessionId: string = '';
  private config: ChunkUploadConfig;
  // 全局累计计数（跨所有文件），让进度条 0%→100% 连续而不是 per file 重置
  private totalChunksAll: number = 0;
  private uploadedChunksAll: number = 0;

  constructor(config: ChunkUploadConfig) {
    this.config = config;
  }

  /**
   * 执行上传
   */
  async upload(): Promise<ChunkUploadResult> {
    const { files, datasetId } = this.config;

    // 算所有文件总 chunk 数（进度条分母）
    this.totalChunksAll = files.reduce(
      (sum, f) => sum + Math.ceil(f.size / CHUNK_SIZE_BYTES),
      0,
    );
    this.uploadedChunksAll = 0;

    // 步骤 1: 初始化上传会话
    try {
      const initResult = await this.initSession(datasetId, files);
      // 响应拦截器返回的是 AxiosResponse 对象，需要访问 .data
      this.sessionId = initResult.data?.session_id;
      if (!this.sessionId) {
        throw new Error('session_id not found in response');
      }
    } catch (error) {
      return { success: false, error: '初始化上传会话失败' };
    }

    // 步骤 2: 上传所有分片
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await this.uploadFile(file, i);
      } catch (error) {
        return { success: false, error: `文件 ${file.name} 上传失败` };
      }
    }

    // 步骤 3: 完成上传，触发后端组装
    try {
      const response = await this.finishSession(datasetId);
      // 响应拦截器返回的是 AxiosResponse 对象，需要访问 .data
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: '组装文件失败' };
    }
  }

  /**
   * 初始化上传会话
   */
  private async initSession(datasetId: string, files: File[]): Promise<any> {
    const { post } = Http.use();

    // 计算总分片数
    let totalChunks = 0;
    files.forEach((file) => {
      totalChunks += Math.ceil(file.size / CHUNK_SIZE_BYTES);
    });

    return post(`/datasets/${datasetId}/files-chunked/init`, {
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        chunks: Math.ceil(f.size / CHUNK_SIZE_BYTES),
      })),
      total_chunks: totalChunks,
    });
  }

  /**
   * 上传单个文件（分片）
   */
  private async uploadFile(file: File, fileIndex: number): Promise<any> {
    const { formData: originalFormData, onProgress } = this.config;

    const fileTotalChunks = Math.ceil(file.size / CHUNK_SIZE_BYTES);

    for (let chunkIndex = 0; chunkIndex < fileTotalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE_BYTES;
      const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
      const chunk = file.slice(start, end);

      try {
        await this.uploadChunkWithRetry(
          this.config.datasetId,
          fileIndex,
          chunkIndex,
          fileTotalChunks,
          chunk,
          file.name,
          file.size,
          originalFormData
        );

        this.uploadedChunksAll++;

        // 进度按全局累计算，3 个文件的整体进度 0%→100% 走一次
        onProgress?.({
          fileIndex,
          fileName: file.name,
          totalFiles: this.config.files.length,
          uploadedChunks: this.uploadedChunksAll,
          totalChunks: this.totalChunksAll,
          percentage: Math.floor((this.uploadedChunksAll / this.totalChunksAll) * 100),
        });
      } catch (error) {
        throw new Error(`分片 ${chunkIndex + 1}/${fileTotalChunks} 上传失败`);
      }
    }
  }

  /**
   * 带重试的单分片上传：网络层错误 / 5xx 自动指数退避重试，4xx 立即失败
   */
  private async uploadChunkWithRetry(
    datasetId: string,
    fileIndex: number,
    chunkIndex: number,
    chunkTotal: number,
    chunk: Blob,
    fileName: string,
    fileSize: number,
    originalFormData?: FormData
  ): Promise<void> {
    let lastError: any;
    for (let attempt = 1; attempt <= CHUNK_RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        await this.uploadChunk(
          datasetId,
          fileIndex,
          chunkIndex,
          chunkTotal,
          chunk,
          fileName,
          fileSize,
          originalFormData
        );
        return;
      } catch (error) {
        lastError = error;
        if (!isRetryableChunkError(error) || attempt >= CHUNK_RETRY_MAX_ATTEMPTS) {
          throw error;
        }
        const delay = CHUNK_RETRY_DELAYS_MS[attempt - 1] ?? 8000;
        // eslint-disable-next-line no-console
        console.warn(
          `[chunk-uploader] 分片 ${chunkIndex + 1}/${chunkTotal} 第 ${attempt} 次失败，${delay}ms 后重试`,
          error
        );
        await sleep(delay);
      }
    }
    throw lastError;
  }

  /**
   * 上传单个分片
   */
  private async uploadChunk(
    datasetId: string,
    fileIndex: number,
    chunkIndex: number,
    chunkTotal: number,
    chunk: Blob,
    fileName: string,
    fileSize: number,
    originalFormData?: FormData
  ): Promise<void> {
    const { post } = Http.use();

    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('file_index', fileIndex.toString());
    formData.append('chunk_index', chunkIndex.toString());
    formData.append('chunk_total', chunkTotal.toString());
    formData.append('file', chunk);
    formData.append('file_name', fileName);
    formData.append('file_size', fileSize.toString());

    // 从原始 FormData 中复制非文件字段
    if (originalFormData) {
      for (const [key, value] of Array.from(originalFormData.entries())) {
        if (key !== 'files' && !(value instanceof File)) {
          formData.append(key, value as string);
        }
      }
    }

    await post(`/datasets/${datasetId}/files-chunked/chunk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * 完成上传会话，触发后端组装
   */
  private async finishSession(datasetId: string): Promise<any> {
    const { post } = Http.use();
    const { formData: originalFormData } = this.config;

    const formData = new FormData();
    formData.append('session_id', this.sessionId);

    // 从原始 FormData 中复制非文件字段
    if (originalFormData) {
      for (const [key, value] of Array.from(originalFormData.entries())) {
        if (key !== 'files' && !(value instanceof File)) {
          formData.append(key, value as string);
        }
      }
    }

    // 这个请求会等待后端组装完成并返回最终结果
    return post(`/datasets/${datasetId}/files-chunked/finish`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

/**
 * 判断是否应该使用分片上传
 */
export function shouldUseChunkUpload(fileSize: number): boolean {
  return CHUNK_UPLOAD_ENABLED && fileSize > THRESHOLD_BYTES;
}

/**
 * 判断多个文件是否应该使用分片上传
 * 判断依据：所有文件的总大小是否超过阈值
 */
export function shouldUseChunkUploadForFiles(files: File[] | FileList): boolean {
  if (!CHUNK_UPLOAD_ENABLED) return false;

  // 计算所有文件的总大小
  let totalSize = 0;
  for (let i = 0; i < files.length; i++) {
    totalSize += files[i].size;
  }

  return totalSize > THRESHOLD_BYTES;
}

/**
 * 执行分片上传
 */
export async function uploadWithChunks(config: ChunkUploadConfig): Promise<ChunkUploadResult> {
  const uploader = new ChunkUploader(config);
  return uploader.upload();
}

/**
 * 获取配置（用于调试）
 */
export function getChunkUploadConfig() {
  return {
    enabled: CHUNK_UPLOAD_ENABLED,
    thresholdMB: CHUNK_UPLOAD_THRES_MB,
    chunkSizeMB: CHUNK_UPLOAD_SIZE_MB,
  };
}
